import { ApprovalStatus, type CompletionCondition } from '@insio/shared-types';
import type { BadgeProps } from '@insio/ui-toolkit';
import { AwaitingCompletionStatus, awaitingCompletionStatus } from './awaiting-completion-status';
import { CANCELLED_BADGE_COLOR, COMPLETED_BADGE_COLOR } from './procurement-status';

/**
 * The status a requisition shows: what it waits for, not what happened. Derived from `approvalStatus`,
 * `isCancelled`, `isCompleted`, `isOrdered` and `unmetCompletionConditions`.
 */
export const RequisitionDisplayStatus = {
  DRAFT: 'DRAFT',
  FOR_APPROVAL: 'FOR_APPROVAL',
  /** Approved, with a line no order covers yet. */
  TO_ORDER: 'TO_ORDER',
  /** Every line ordered: what it still waits for, shared with the order's rule. */
  ...AwaitingCompletionStatus,
  REJECTED: 'REJECTED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED'
} as const;

export type RequisitionDisplayStatus =
  (typeof RequisitionDisplayStatus)[keyof typeof RequisitionDisplayStatus];

/** Minimal requisition shape needed to derive the displayed status. */
export interface RequisitionDisplayStatusSource {
  readonly approvalStatus: ApprovalStatus;
  readonly isCancelled: boolean;
  readonly isCompleted: boolean;
  /** Every line is covered by an order; the approval status stays APPROVED. */
  readonly isOrdered: boolean;
  /** Left out (a shape without it) reads as no named condition. */
  readonly unmetCompletionConditions?: readonly CompletionCondition[] | null;
}

/** What a requisition shows before approval; the automation and a user deciding look alike. */
const SAME_NAMED: ReadonlyMap<string, RequisitionDisplayStatus> = new Map([
  [ApprovalStatus.UNPROCESSED, RequisitionDisplayStatus.DRAFT],
  [ApprovalStatus.PENDING, RequisitionDisplayStatus.FOR_APPROVAL],
  [ApprovalStatus.FOR_REVIEW, RequisitionDisplayStatus.FOR_APPROVAL],
  [ApprovalStatus.REJECTED, RequisitionDisplayStatus.REJECTED]
]);

/** Derive the one status a requisition shows: cancelled › completed › what it waits for. */
export function requisitionDisplayStatus(
  source: RequisitionDisplayStatusSource
): RequisitionDisplayStatus {
  if (source.isCancelled) return RequisitionDisplayStatus.CANCELLED;
  if (source.isCompleted) return RequisitionDisplayStatus.COMPLETED;
  if (source.approvalStatus === ApprovalStatus.APPROVED) {
    // A partly covered requisition still has something to order.
    return source.isOrdered
      ? awaitingCompletionStatus(source.unmetCompletionConditions)
      : RequisitionDisplayStatus.TO_ORDER;
  }
  // A status the map does not know — an older row, a newer server — is something nobody has
  // finished with, so it reads as work in hand rather than as an empty badge.
  return SAME_NAMED.get(source.approvalStatus) ?? RequisitionDisplayStatus.DRAFT;
}

/** Badge colour for a displayed status — one map, so a requisition reads the same everywhere. */
export const requisitionDisplayStatusColor = (
  status: RequisitionDisplayStatus
): NonNullable<BadgeProps['color']> => DISPLAY_STATUS_COLOR[status] ?? 'neutral';

const DISPLAY_STATUS_COLOR: Readonly<
  Record<RequisitionDisplayStatus, NonNullable<BadgeProps['color']>>
> = {
  [RequisitionDisplayStatus.DRAFT]: 'neutral',
  [RequisitionDisplayStatus.FOR_APPROVAL]: 'warning',
  [RequisitionDisplayStatus.TO_ORDER]: 'success',
  // Ordered and under way, as an order waiting for the same reads.
  [RequisitionDisplayStatus.AWAITING_DELIVERY]: 'info',
  [RequisitionDisplayStatus.AWAITING_INVOICE]: 'info',
  [RequisitionDisplayStatus.AWAITING_DELIVERY_AND_INVOICE]: 'info',
  [RequisitionDisplayStatus.AWAITING_COMPLETION]: 'info',
  [RequisitionDisplayStatus.REJECTED]: 'error',
  [RequisitionDisplayStatus.COMPLETED]: COMPLETED_BADGE_COLOR,
  [RequisitionDisplayStatus.CANCELLED]: CANCELLED_BADGE_COLOR
};
